import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import ProdottiPanel from "./ProdottiPanel";
import MessaggiPanel from "./MessaggiPanel";
import ImpostazioniPanel from "./ImpostazioniPanel";

function formatError(e) {
    const d = e?.response?.data?.detail;
    if (d == null) return "Qualcosa è andato storto. Riprova.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
    return String(d);
}

function Login({ onDone }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errore, setErrore] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErrore("");
        setLoading(true);
        try {
            const res = await api.post("/auth/login", { email, password });
            onDone(res.data);
        } catch (err) {
            setErrore(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full bg-background border border-line px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/60";

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-5">
            <div className="w-full max-w-sm">
                <div className="flex justify-center">
                    <Logo />
                </div>
                <div className="mt-8 border border-line bg-card p-8">
                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary text-center">
                        Area Riservata
                    </p>
                    <h1 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-center">
                        Accesso gestore
                    </h1>
                    <form onSubmit={submit} className="mt-6 space-y-3">
                        <input
                            data-testid="admin-login-email"
                            className={inputCls}
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <input
                            data-testid="admin-login-password"
                            className={inputCls}
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        {errore && (
                            <p data-testid="admin-login-errore" className="text-xs text-destructive">
                                {errore}
                            </p>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-login-submit"
                            disabled={loading}
                            className="w-full bg-primary text-primary-foreground py-3.5 font-display font-bold text-sm tracking-tight hover:bg-primary/90 transition-colors disabled:opacity-60"
                        >
                            {loading ? "Accesso…" : "Entra"}
                        </button>
                    </form>
                </div>
                <p className="mt-6 text-center">
                    <a
                        href="/"
                        data-testid="admin-back-site"
                        className="font-mono text-xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
                    >
                        ← Torna al sito
                    </a>
                </p>
            </div>
        </div>
    );
}

export default function AdminApp() {
    const [user, setUser] = useState(null);
    const [tab, setTab] = useState("prodotti");

    useEffect(() => {
        api.get("/auth/me")
            .then((res) => setUser(res.data))
            .catch(() => setUser(false));
    }, []);

    if (user === null) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
                    Caricamento…
                </p>
            </div>
        );
    }

    if (!user) return <Login onDone={setUser} />;

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setUser(false);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-panel">
            <header className="border-b border-line bg-card">
                <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <a href="/" aria-label="Torna al sito">
                            <Logo markClass="h-9 w-9" showText={false} />
                        </a>
                        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hidden sm:block">
                            Area Riservata
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            data-testid="admin-visualizza-sito"
                            className="border border-line px-4 py-2 font-mono text-xs tracking-[0.1em] uppercase hover:border-primary hover:text-primary transition-colors"
                        >
                            Vedi sito
                        </a>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="bg-foreground text-background px-4 py-2 font-mono text-xs tracking-[0.1em] uppercase hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                            Esci
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
                <div className="flex flex-wrap gap-2">
                    {[
                        { id: "prodotti", label: "Prodotti" },
                        { id: "prenotazioni", label: "Prenotazioni" },
                        { id: "messaggi", label: "Messaggi" },
                        { id: "impostazioni", label: "Impostazioni" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-2.5 font-mono text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                                tab === t.id
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "border-line text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                    {tab === "prodotti" && <ProdottiPanel />}
                    {tab === "prenotazioni" && <MessaggiPanel mode="prenotazioni" />}
                    {tab === "messaggi" && <MessaggiPanel mode="messaggi" />}
                    {tab === "impostazioni" && <ImpostazioniPanel />}
                </div>
            </div>
        </div>
    );
}